import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Button,
  Tag,
  Tooltip,
  Space,
  Typography,
  Spin,
  Alert,
  Rate,
  Modal,
  Descriptions,
  DatePicker
} from 'antd';
import type { Dayjs } from 'dayjs';
import {
  EyeOutlined,
  FilterOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../config/api';

const { Title } = Typography;
const { Option } = Select;

interface FeedbackItem {
  id: string;
  ticket_id: string;
  rating: number;
  comment: string;
  is_submitted: boolean;
  submitted_at: string;
  created_at: string;
  ticket_number: string;
  ticket_subject: string;
  assignee_first_name: string;
  assignee_last_name: string;
  business_id: string;
  business_name: string;
  contact_person: string;
  business_email: string;
  archived_at?: string;
  is_archived?: boolean;
}

const FeedbackDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [filters, setFilters] = useState({
    is_submitted: undefined as boolean | undefined,
    rating: undefined as number | undefined,
    assignee_id: undefined as string | undefined
  });
  const [ratingFilter, setRatingFilter] = useState<'positive' | 'negative' | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  
  // Get business_id and business_name from URL params
  const businessIdFromUrl = searchParams.get('business_id');
  const businessNameFromUrl = searchParams.get('business_name');
  const [storeTicketsModalVisible, setStoreTicketsModalVisible] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(businessIdFromUrl);
  const [selectedBusinessName, setSelectedBusinessName] = useState<string>(businessNameFromUrl || '');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  
  // Auto-open modal if business_id is in URL (e.g., when returning from a ticket)
  useEffect(() => {
    if (businessIdFromUrl && businessNameFromUrl) {
      setSelectedBusinessId(businessIdFromUrl);
      setSelectedBusinessName(businessNameFromUrl);
      setStoreTicketsModalVisible(true);
    }
  }, [businessIdFromUrl, businessNameFromUrl]);

  // Fetch feedback data
  const { data: feedbackData, isLoading, error, refetch } = useQuery(
    ['feedback', filters, dateRange],
    async () => {
      const params = new URLSearchParams();
      if (filters.is_submitted !== undefined) {
        params.append('is_submitted', filters.is_submitted.toString());
      }
      if (filters.rating !== undefined) {
        params.append('rating', filters.rating.toString());
      }
      if (filters.assignee_id) {
        params.append('assignee_id', filters.assignee_id);
      }
      // Add date range to API call - this will include archived feedback
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
      }
      
      const response = await api.get(`/api/feedback?${params.toString()}`);
      return response.data;
    }
  );

  // Fetch agents for assignee filter
  const { data: agentsData } = useQuery(
    ['agents'],
    async () => {
      const response = await api.get('/api/users/agents');
      return response.data;
    }
  );

  // Auto-refresh every 10 seconds for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 10000);
    return () => clearInterval(interval);
  }, [refetch]);

  // Fetch feedback statistics
  const { data: statsData } = useQuery(
    ['feedback-stats'],
    async () => {
      const response = await api.get('/api/feedback/stats');
      return response.data;
    }
  );

  // Fetch monthly feedback statistics
  const { data: monthlyStatsData } = useQuery(
    ['feedback-monthly-stats'],
    async () => {
      const response = await api.get('/api/feedback/stats/monthly');
      return response.data;
    }
  );

  const allFeedback = feedbackData?.items || [];
  
  // Apply filters (date filtering is now done on backend)
  let filteredFeedback = allFeedback;
  
  // Apply rating filter (positive = 4-5, negative = 1-2)
  if (ratingFilter === 'positive') {
    filteredFeedback = filteredFeedback.filter((item: FeedbackItem) => 
      item.is_submitted && item.rating && item.rating >= 4 && item.rating <= 5
    );
  } else if (ratingFilter === 'negative') {
    filteredFeedback = filteredFeedback.filter((item: FeedbackItem) => 
      item.is_submitted && item.rating && item.rating >= 1 && item.rating <= 2
    );
  }
  
  const feedback = filteredFeedback;
  
  const stats = statsData;
  const agents = agentsData?.users || [];

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'success';
    if (rating >= 3) return 'warning';
    return 'error';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTicketClick = (ticketNumber: string) => {
    navigate(`/workspace/number/${ticketNumber}`);
  };

  const handleStoreClick = (businessId: string, businessName: string) => {
    setSelectedBusinessId(businessId);
    setSelectedBusinessName(businessName);
    setStoreTicketsModalVisible(true);
    // Update URL params to persist state
    const params = new URLSearchParams(searchParams);
    params.set('business_id', businessId);
    params.set('business_name', businessName);
    setSearchParams(params, { replace: true });
  };

  // Fetch tickets for selected business
  const { data: storeTicketsData, isLoading: isLoadingStoreTickets } = useQuery(
    ['store-tickets', selectedBusinessId],
    async () => {
      if (!selectedBusinessId) return null;
      const response = await api.get('/api/tickets', {
        params: { business_id: selectedBusinessId, limit: 100 }
      });
      return response.data;
    },
    {
      enabled: !!selectedBusinessId && storeTicketsModalVisible
    }
  );

  const storeTickets = storeTicketsData?.tickets || [];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description="Failed to load feedback data. Please try again."
        type="error"
        showIcon
      />
    );
  }

  const monthlyStats = monthlyStatsData;
  const currentMonth = monthlyStats?.current_month;
  const lastMonth = monthlyStats?.last_month;

  const formatMonthName = (monthData: any) => {
    // Use month_formatted from backend if available (avoids timezone issues)
    if (monthData?.month_formatted) {
      return monthData.month_formatted;
    }
    // Fallback to parsing the timestamp
    if (monthData?.month) {
      const date = new Date(monthData.month);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return '';
  };

  return (
    <div style={{ marginLeft: '24px', marginRight: '24px' }}>
      <Title level={2}>Customer Feedback Dashboard</Title>

      {/* Monthly Statistics Comparison */}
      {monthlyStats && (
        <Card 
          title="Monthly Statistics Comparison" 
          style={{ marginBottom: '24px' }}
        >
          <Row gutter={[24, 16]}>
            {/* Current Month Column */}
            <Col xs={24} md={12}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0f9ff', 
                border: '2px solid #1890ff',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                  color: '#1890ff',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  📅 {formatMonthName(currentMonth)} (This Month)
                </div>
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Total Feedback"
                        value={currentMonth?.total_feedback || 0}
                        prefix={<StarOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Average Rating"
                        value={currentMonth?.average_rating ? Number(currentMonth.average_rating).toFixed(1) : '0.0'}
                        suffix="/ 5"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Positive (4-5 stars)"
                        value={currentMonth?.positive_feedback || 0}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Negative (1-2 stars)"
                        value={currentMonth?.negative_feedback || 0}
                        valueStyle={{ color: '#ff4d4f' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>
            </Col>

            {/* Last Month Column */}
            <Col xs={24} md={12}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fafafa', 
                border: '2px solid #d9d9d9',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                  color: '#595959',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  📅 {formatMonthName(lastMonth)} (Last Month)
                </div>
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Total Feedback"
                        value={lastMonth?.total_feedback || 0}
                        prefix={<StarOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Average Rating"
                        value={lastMonth?.average_rating ? Number(lastMonth.average_rating).toFixed(1) : '0.0'}
                        suffix="/ 5"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Positive (4-5 stars)"
                        value={lastMonth?.positive_feedback || 0}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Negative (1-2 stars)"
                        value={lastMonth?.negative_feedback || 0}
                        valueStyle={{ color: '#ff4d4f' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Feedback"
                value={stats.total_feedback || 0}
                prefix={<StarOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Average Rating"
                value={stats.average_rating ? Number(stats.average_rating).toFixed(1) : '0.0'}
                suffix="/ 5"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              style={{ cursor: 'pointer', transition: 'all 0.3s' }}
              hoverable
              onClick={() => {
                setRatingFilter(ratingFilter === 'positive' ? null : 'positive');
                setFilters(prev => ({ ...prev, rating: undefined }));
              }}
              bodyStyle={{ 
                backgroundColor: ratingFilter === 'positive' ? '#f6ffed' : undefined,
                border: ratingFilter === 'positive' ? '2px solid #52c41a' : undefined
              }}
            >
              <Statistic
                title="Positive (4-5 stars)"
                value={stats.positive_feedback || 0}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              style={{ cursor: 'pointer', transition: 'all 0.3s' }}
              hoverable
              onClick={() => {
                setRatingFilter(ratingFilter === 'negative' ? null : 'negative');
                setFilters(prev => ({ ...prev, rating: undefined }));
              }}
              bodyStyle={{ 
                backgroundColor: ratingFilter === 'negative' ? '#fff1f0' : undefined,
                border: ratingFilter === 'negative' ? '2px solid #ff4d4f' : undefined
              }}
            >
              <Statistic
                title="Negative (1-2 stars)"
                value={stats.negative_feedback || 0}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Space wrap>
          <FilterOutlined />
          <DatePicker.RangePicker
            placeholder={['Start Date', 'End Date']}
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
            allowClear
            style={{ width: 250 }}
          />
          <Select
            placeholder="Status"
            style={{ width: 150 }}
            value={filters.is_submitted === undefined ? undefined : filters.is_submitted}
            onChange={(value) => setFilters(prev => ({ 
              ...prev, 
              is_submitted: value === undefined ? undefined : value === true
            }))}
            allowClear
          >
            <Option value={true}>Submitted</Option>
            <Option value={false}>Pending</Option>
          </Select>
          
          <Select
            placeholder="Rating"
            style={{ width: 150 }}
            value={filters.rating}
            onChange={(value) => {
              setFilters(prev => ({ 
                ...prev, 
                rating: value
              }));
              setRatingFilter(null); // Clear positive/negative filter when selecting specific rating
            }}
            allowClear
          >
            <Option value={5}>5 Stars</Option>
            <Option value={4}>4 Stars</Option>
            <Option value={3}>3 Stars</Option>
            <Option value={2}>2 Stars</Option>
            <Option value={1}>1 Star</Option>
          </Select>

          <Select
            placeholder="Assignee"
            style={{ width: 200 }}
            value={filters.assignee_id}
            onChange={(value) => setFilters(prev => ({ 
              ...prev, 
              assignee_id: value
            }))}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {agents.map((agent: any) => (
              <Option key={agent.id} value={agent.id}>
                {agent.first_name} {agent.last_name}
              </Option>
            ))}
          </Select>
        </Space>
      </Card>

      {/* Feedback Table */}
      <Table
        dataSource={feedback}
        rowKey="id"
        columns={[
          {
            title: 'Store',
            dataIndex: 'business_name',
            key: 'store',
            render: (text: string, record: FeedbackItem) => (
              <div>
                <button
                  type="button"
                  onClick={() => record.business_id && handleStoreClick(record.business_id, text)}
                  disabled={!record.business_id}
                  style={{ 
                    cursor: record.business_id ? 'pointer' : 'default', 
                    color: record.business_id ? '#1890ff' : 'inherit',
                    fontWeight: 500,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    textAlign: 'left',
                  }}
                >
                  {text}
                </button>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.contact_person}
                </div>
              </div>
            ),
          },
          {
            title: 'Ticket',
            dataIndex: 'ticket_number',
            key: 'ticket',
            render: (text: string, record: FeedbackItem) => (
              <div>
                <button
                  type="button"
                  onClick={() => handleTicketClick(record.ticket_number)}
                  style={{ cursor: 'pointer', color: '#1890ff', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                >
                  #{text}
                </button>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.ticket_subject}
                </div>
              </div>
            ),
          },
          {
            title: 'Assignee',
            key: 'assignee',
            render: (_, record: FeedbackItem) => 
              record.assignee_first_name && record.assignee_last_name 
                ? `${record.assignee_first_name} ${record.assignee_last_name}`
                : 'Unassigned',
          },
          {
            title: 'Rating',
            dataIndex: 'rating',
            key: 'rating',
            render: (rating: number, record: FeedbackItem) => 
              record.is_submitted ? (
                <Space>
                  <Rate disabled value={rating} />
                  <Tag color={getRatingColor(rating)}>{rating}</Tag>
                </Space>
              ) : (
                <span style={{ color: '#999' }}>Not rated</span>
              ),
          },
          {
            title: 'Comment',
            dataIndex: 'comment',
            key: 'comment',
            render: (comment: string) => 
              comment ? (
                <div style={{ maxWidth: 200 }}>
                  {comment.length > 100 ? `${comment.substring(0, 100)}...` : comment}
                </div>
              ) : (
                <span style={{ color: '#999' }}>No comment</span>
              ),
          },
          {
            title: 'Date',
            dataIndex: 'submitted_at',
            key: 'date',
            render: (date: string, record: FeedbackItem) => 
              formatDate(date || record.created_at),
          },
              {
                title: 'Status',
                dataIndex: 'is_submitted',
                key: 'status',
                render: (isSubmitted: boolean, record: FeedbackItem) => (
                  <Space>
                    <Tag color={isSubmitted ? 'success' : 'warning'}>
                      {isSubmitted ? 'Submitted' : 'Pending'}
                    </Tag>
                    {record.is_archived && (
                      <Tag color="default">Archived</Tag>
                    )}
                  </Space>
                ),
              },
          {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: FeedbackItem) => (
              <Tooltip title="View Details">
                <Button 
                  type="text" 
                  icon={<EyeOutlined />} 
                  size="small" 
                  onClick={() => {
                    setSelectedFeedback(record);
                    setModalVisible(true);
                  }}
                />
              </Tooltip>
            ),
          },
        ]}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        locale={{
          emptyText: 'No feedback found matching your filters.',
        }}
      />

      {/* Feedback Details Modal */}
      <Modal
        title="Feedback Details"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedFeedback(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setModalVisible(false);
            setSelectedFeedback(null);
          }}>
            Close
          </Button>,
          selectedFeedback && (
            <Button 
              key="ticket" 
              type="primary" 
              onClick={() => {
                navigate(`/workspace/number/${selectedFeedback.ticket_number}`);
                setModalVisible(false);
                setSelectedFeedback(null);
              }}
            >
              View Ticket
            </Button>
          ),
        ]}
        width={700}
      >
        {selectedFeedback && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Ticket">
              <Space>
                <button
                  type="button"
                  onClick={() => {
                    navigate(`/workspace/number/${selectedFeedback.ticket_number}`);
                    setModalVisible(false);
                    setSelectedFeedback(null);
                  }}
                  style={{ cursor: 'pointer', color: '#1890ff', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                >
                  #{selectedFeedback.ticket_number}
                </button>
                <span style={{ color: '#666' }}>{selectedFeedback.ticket_subject}</span>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Business">
              <div>
                <div style={{ fontWeight: 500 }}>{selectedFeedback.business_name}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {selectedFeedback.contact_person}
                </div>
                {selectedFeedback.business_email && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedFeedback.business_email}
                  </div>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Assignee">
              {selectedFeedback.assignee_first_name && selectedFeedback.assignee_last_name
                ? `${selectedFeedback.assignee_first_name} ${selectedFeedback.assignee_last_name}`
                : 'Unassigned'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Space>
                <Tag color={selectedFeedback.is_submitted ? 'success' : 'warning'}>
                  {selectedFeedback.is_submitted ? 'Submitted' : 'Pending'}
                </Tag>
                {selectedFeedback.is_archived && (
                  <Tag color="default">Archived</Tag>
                )}
              </Space>
            </Descriptions.Item>
            {selectedFeedback.is_submitted && (
              <>
                <Descriptions.Item label="Rating">
                  <Space>
                    <Rate disabled value={selectedFeedback.rating || 0} />
                    <Tag color={getRatingColor(selectedFeedback.rating || 0)}>
                      {selectedFeedback.rating} / 5
                    </Tag>
                  </Space>
                </Descriptions.Item>
                {selectedFeedback.comment && (
                  <Descriptions.Item label="Comment">
                    <div style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                      {selectedFeedback.comment}
                    </div>
                  </Descriptions.Item>
                )}
              </>
            )}
            <Descriptions.Item label="Feedback Request Sent">
              {formatDate(selectedFeedback.created_at)}
            </Descriptions.Item>
            {selectedFeedback.submitted_at && (
              <Descriptions.Item label="Feedback Submitted">
                {formatDate(selectedFeedback.submitted_at)}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Store Tickets Modal */}
      <Modal
        title={`Tickets for ${selectedBusinessName}`}
        open={storeTicketsModalVisible}
        onCancel={() => {
          setStoreTicketsModalVisible(false);
          setSelectedBusinessId(null);
          setSelectedBusinessName('');
          // Remove business params from URL
          const params = new URLSearchParams(searchParams);
          params.delete('business_id');
          params.delete('business_name');
          setSearchParams(params, { replace: true });
        }}
        footer={[
          <Button key="close" onClick={() => {
            setStoreTicketsModalVisible(false);
            setSelectedBusinessId(null);
            setSelectedBusinessName('');
            // Remove business params from URL
            const params = new URLSearchParams(searchParams);
            params.delete('business_id');
            params.delete('business_name');
            setSearchParams(params, { replace: true });
          }}>
            Close
          </Button>
        ]}
        width={900}
      >
        {isLoadingStoreTickets ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : storeTickets.length === 0 ? (
          <Alert
            message="No tickets found"
            description={`No tickets found for ${selectedBusinessName}.`}
            type="info"
            showIcon
          />
        ) : (
          <Table
            dataSource={storeTickets}
            rowKey="id"
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tickets`,
            }}
            columns={[
              {
                title: 'Ticket #',
                dataIndex: 'ticket_number',
                key: 'ticket_number',
                render: (text: string) => (
                  <button
                    type="button"
                    onClick={() => {
                      // Navigate to ticket but preserve return state with business info
                      navigate(`/workspace/number/${text}`, {
                        state: {
                          returnTo: `/feedback-dashboard?business_id=${selectedBusinessId}&business_name=${encodeURIComponent(selectedBusinessName)}`
                        }
                      });
                    }}
                    style={{ cursor: 'pointer', color: '#1890ff', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                  >
                    #{text}
                  </button>
                ),
              },
              {
                title: 'Subject',
                dataIndex: 'subject',
                key: 'subject',
                ellipsis: true,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status: string) => {
                  const statusColors: Record<string, string> = {
                    new: 'default',
                    open: 'blue',
                    pending: 'orange',
                    solved: 'green',
                  };
                  return (
                    <Tag color={statusColors[status] || 'default'}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Tag>
                  );
                },
              },
              {
                title: 'Priority',
                dataIndex: 'priority',
                key: 'priority',
                render: (priority: string) => {
                  const priorityColors: Record<string, string> = {
                    low: 'green',
                    medium: 'orange',
                    high: 'red',
                    urgent: 'red',
                  };
                  return (
                    <Tag color={priorityColors[priority] || 'default'}>
                      {priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'N/A'}
                    </Tag>
                  );
                },
              },
              {
                title: 'Assignee',
                key: 'assignee',
                render: (_, record: any) => {
                  if (record.assigned_to_name && record.assigned_to_last_name) {
                    return `${record.assigned_to_name} ${record.assigned_to_last_name}`;
                  }
                  return <Tag color="default">Unassigned</Tag>;
                },
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                key: 'created_at',
                render: (date: string) => formatDate(date),
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default FeedbackDashboard;
